import React, { useCallback, useMemo } from 'react';
import { useClientUser } from '@/hooks';

import { validate, genAttributes } from '@onward-delivery/core';
import { stripPlaceholderPhone } from '@/utilities/formatPhoneNumber';
import { genAttributesPerTab } from './modal';
import { useServiceCatalogs } from './serviceCatalog';
import {
    getInvalidServiceLines,
    getMissingServiceCatalogFields,
    getServiceCatalogCacheStateForOrder,
    getServiceCatalogForOrder,
} from '../utilities/serviceCatalog';
import {
    CONSIGNEELESS_ORDER_TYPES,
    DEFAULT_ITEM,
    EDIT_ORDER_TABS,
    hiddenTabsForOrderType,
    PROGRESSION_TYPES,
    SHIPMENT_FORM_MODAL_PROGRESSION,
    SHIPMENT_FORM_STATE,
    SHIPMENT_FORM_TYPES,
} from '../constants';
import { getJsonImportType } from '../constants/jsonImportTypes';
import {
    processOrdersFromCSV,
    processOrdersFromPDF,
    createOrder,
    mapPickupInfo,
    findDuplicateLineIds,
} from '../utilities/processOrders';
import { processOrdersFromJSON } from '../utilities/processOrdersFromJSON';
import { isReviewGeocodingStatus } from '../modals/EditOrderModal/geocoding';
import { missingShipperFields } from '../utilities/requiredFields';
import _ from 'lodash';

// Tabs the per-order EditOrderModal will actually render in the import flow.
// Mirrors the `tabs` memo in ShipmentForm/index.jsx so per-order validation
// only flags fields the user can reach. Shipment-level fields (e.g., the
// warehouse leg when groupedPickup is off) are managed via PickupInfoModal,
// not the per-order modal, so they must not surface as per-order errors.
//
// Exported for unit testing; importers outside `__test__/` should prefer
// `useOrderErrors` which composes this with `missingFieldsByReachableTab`.
export const reachableImportTabs = (state, orderType = state?.pickupInfo?.order_type) => {
    const reachable = new Set([
        EDIT_ORDER_TABS.ORDER_DETAILS,
        EDIT_ORDER_TABS.CUSTOMER,
        EDIT_ORDER_TABS.ITEMS,
        EDIT_ORDER_TABS.SERVICES,
        EDIT_ORDER_TABS.NOTES,
        EDIT_ORDER_TABS.WAREHOUSE,
        EDIT_ORDER_TABS.HAUL_AWAY,
    ]);
    if (state?.pickupInfo?.is_middle_mile_origin) reachable.add(EDIT_ORDER_TABS.ORIGIN);
    if (state?.groupedPickup) reachable.add(EDIT_ORDER_TABS.PICKUP);
    for (const tab of hiddenTabsForOrderType(orderType)) reachable.delete(tab);
    return reachable;
};

// Project fieldsByTab + raw missing fields onto only the tabs the user can
// reach. Fields that fall outside any reachable tab are dropped (they're
// shipment-level, not user-fixable per-order). Exported for unit testing;
// the production call site is `useOrderErrors` below.
export const missingFieldsByReachableTab = (fieldsByTab, hasMissingField, reachable) => {
    const reachableFieldsByTab = Object.entries(fieldsByTab).filter(([tab]) => reachable.has(tab));
    const reachableFields = new Set(reachableFieldsByTab.flatMap(([, fields]) => fields));
    const reachableMissing = hasMissingField.filter((f) => reachableFields.has(f));
    return {
        reachableMissing,
        byTab: Object.fromEntries(
            reachableFieldsByTab.map(([tab, fields]) => [tab, fields.some((field) => reachableMissing.includes(field))])
        ),
    };
};

const SOURCE_FORM_BY_TYPE = {
    [SHIPMENT_FORM_TYPES.CSV]: 'CSV UPLOAD',
    [SHIPMENT_FORM_TYPES.JSON]: 'JSON UPLOAD',
};
const JOB_TYPE_BY_FORM = {
    [SHIPMENT_FORM_TYPES.CSV]: 'CSV',
    [SHIPMENT_FORM_TYPES.JSON]: 'JSON',
};
// PDF and Manual both fall through to MANUAL, matching the pre-refactor submit().
const sourceFormForType = (formType) => SOURCE_FORM_BY_TYPE[formType] || 'MANUAL';
const jobTypeForForm = (formType) => JOB_TYPE_BY_FORM[formType] || 'MANUAL';

// Shared payload builder for the Footer submit path. Strips the display-only
// *_formatted fields + client-side pricing scratch, stamps the create-time
// fields (oms, shipper, source, status), and strips display-only placeholder phones.
// Accepts a keyed orders object or an array.
export const finalizeOrders = (ordersObj, { oms, formType, templateName, userId }) =>
    Object.values(ordersObj || {}).map((order) => {
        const {
            key,
            delivery_date_formatted,
            alternative_delivery_dates_formatted,
            preferred_delivery_date_formatted,
            completion_date_formatted,
            billing_completion_date_formatted,
            estimated_delivery_range_end_formatted,
            estimated_delivery_range_start_formatted,
            pricing_runs,
            last_pricing_run,
            ...remaining
        } = order;

        return {
            ...remaining,
            oms,
            planning: false,
            pickup_phone: stripPlaceholderPhone(order.pickup_phone),
            dropoff_phone: stripPlaceholderPhone(order.dropoff_phone),
            shipper_id: order.shipper_id || userId,
            carrier_id: order.carrier_id || null,
            source_form: sourceFormForType(formType),
            template_name: templateName || null,
            order_status: 'processing',
            ...(remaining?.order_type === 'delivery'
                ? { palletize_returns: false }
                : { palletize_returns: remaining.palletize_returns }),
        };
    });

// Unresolved geocode legs
export const geocodeFailedAddressFields = (order, contact, pickup, origin) => {
    const isEmpty = (val) =>
        val === null || val === undefined || val === '' || val === 0 || val === '0' || Number.isNaN(val);
    const legUnresolved = (attrs) => {
        const geocodeFailed = !!(attrs?.geocode_failed && order[attrs.geocode_failed]);
        const needsReview = !!(attrs?.geocoding && isReviewGeocodingStatus(order.geocoding?.[attrs.geocoding]?.status));
        const savedCoordsMissing =
            !!order.order_id &&
            !!attrs?.lat &&
            !!attrs?.long &&
            (isEmpty(order[attrs.lat]) || isEmpty(order[attrs.long]));

        return geocodeFailed || needsReview || savedCoordsMissing;
    };

    return [
        ...(!CONSIGNEELESS_ORDER_TYPES.includes(order.order_type) && legUnresolved(contact) ? [contact.lat] : []),
        ...(legUnresolved(pickup) ? [pickup.lat] : []),
        ...(order.has_middle_mile_origin && legUnresolved(origin) ? [origin.lat] : []),
    ];
};

export const useOrderErrors = (state) => {
    const { user_id, isCarrierAdmin } = useClientUser();
    const orders = useMemo(() => Object.values(state.orders || {}), [state.orders]);
    const catalogFallback = useMemo(() => ({ shipperId: user_id }), [user_id]);
    const { catalogs, errors } = useServiceCatalogs(orders, catalogFallback);

    return useMemo(() => {
        if (orders.length === 0) {
            return {};
        }

        return Object.fromEntries(
            orders
                .map((order) => {
                    const reachable = reachableImportTabs(state, order.order_type);
                    const serviceCatalog = getServiceCatalogForOrder(order, catalogs, catalogFallback);
                    const missingCatalogFields = getMissingServiceCatalogFields(
                        order,
                        catalogs,
                        errors,
                        catalogFallback
                    );
                    const contact = genAttributes(order);
                    const pickup = genAttributes(order, true);
                    const origin = genAttributes(order, 'ORIGIN');
                    const fieldsByTab = genAttributesPerTab({
                        contact,
                        pickup,
                        origin,
                    });

                    const invalidServiceLines = getInvalidServiceLines(order, serviceCatalog);
                    const [validated, hasMissingField] = validate(order, [], {
                        disableGeocoding: true,
                        invalidServiceLines,
                    });
                    // Tab-level validation extras
                    const missing = [
                        ...new Set([
                            ...(hasMissingField || []),
                            ...missingCatalogFields,
                            ...geocodeFailedAddressFields(order, contact, pickup, origin),
                            ...missingShipperFields(order, { isCarrierAdmin, userId: user_id }),
                        ]),
                    ];
                    // Service errors live on SERVICES
                    const invalidServiceCode =
                        invalidServiceLines.length > 0 && reachable.has(EDIT_ORDER_TABS.SERVICES);
                    if (missing.length > 0 || invalidServiceCode) {
                        const { reachableMissing, byTab } = missingFieldsByReachableTab(
                            fieldsByTab,
                            missing,
                            reachable
                        );
                        if (invalidServiceCode) {
                            byTab[EDIT_ORDER_TABS.SERVICES] = true;
                        }
                        if (reachableMissing.length === 0 && !invalidServiceCode) return;
                        return [validated.key, byTab];
                    }

                    return;
                })
                .filter((x) => !!x)
        );
    }, [orders, state.groupedPickup, state.pickupInfo, catalogs, errors, catalogFallback, isCarrierAdmin, user_id]);
};

export const startJsonUpload = ({ state, callbacks, userId, type }) => {
    callbacks.setLoading(true);
    return processOrdersFromJSON({
        jsonFile: state.uploadedFile.data,
        filename: state.uploadedFile.name,
        freightType: state.freightType,
        userId,
        type,
    })
        .then((res) => {
            callbacks.setSuccessMsg('JSON import started!');
            callbacks.navigate(`/job/${res.job_id}`);
        })
        .catch((err) => {
            console.error(err);
            callbacks.setErrorMsg(err?.message || `Error processing JSON`);
            callbacks.setState(SHIPMENT_FORM_STATE.CREATE);
        })
        .finally(() => {
            callbacks.setLoading(false);
        });
};

export const useShipmentFormCallbacks = (state, callbacks) => {
    const { user_id, circles, locations, email, businessName, businessPhone, service_levels, isCarrierAdmin } =
        useClientUser();
    const orderList = useMemo(() => Object.values(state.orders || {}), [state.orders]);
    const catalogFallback = useMemo(() => ({ shipperId: user_id }), [user_id]);
    const { catalogs, errors } = useServiceCatalogs(orderList, catalogFallback);

    const reset = useCallback(() => {
        if (callbacks.pendingForwardStockingRef) {
            callbacks.pendingForwardStockingRef.current = null;
        }
        callbacks.setFormType(null);
        callbacks.setOrders({});
        callbacks.setMultiPickup(false);
        callbacks.setPickupInfo({});
        callbacks.setMapping({});
        callbacks.setLoading(false);
        callbacks.setUploadedFile(null);
        callbacks.setState(SHIPMENT_FORM_STATE.CREATE);
        callbacks.setFreightType('household');
        callbacks.setProgression({
            current: 0,
            type: null,
        });
    }, []);

    const advance = useCallback(() => {
        if (state.progression.current === SHIPMENT_FORM_MODAL_PROGRESSION[state.progression.type].length - 1) {
            switch (state.progression.type) {
                case PROGRESSION_TYPES.PREPARE_PDF: {
                    callbacks
                        .processPdf({ client_id: user_id, filename: state.pdf.filename, file: state.pdf })
                        .then((resp) => {
                            const order = resp?.data?.order;
                            if (!order) throw new Error('Failed to parse PDF');
                            return processOrdersFromPDF({ order, freightType: state.freightType });
                        })
                        .then((res) => {
                            callbacks.setOrders(res.orders);
                            callbacks.setState(SHIPMENT_FORM_STATE.REVIEW);
                        })
                        .catch((err) => {
                            console.error(err);
                            callbacks.setErrorMsg(err?.message || `Error processing PDF`);
                            callbacks.setState(SHIPMENT_FORM_STATE.CREATE);
                        });
                    break;
                }
                case PROGRESSION_TYPES.PREPARE_CSV_WITH_SAVE:
                case PROGRESSION_TYPES.PREPARE_CSV: {
                    // processOrdersFromCSV takes a single options object; it has no
                    // progress-callback hook.
                    callbacks.setState(SHIPMENT_FORM_STATE.REVIEW);
                    callbacks.setLoading(true);
                    processOrdersFromCSV({
                        csvOptions: state.csvOptions,
                        csvFile: state.uploadedFile.data,
                        csvMapping: state.mapping,
                        pickupInfo: state.pickupInfo,
                        freightType: state.freightType,
                        multiPickup: state.multiPickup,
                        locations,
                        business: {
                            email,
                            name: businessName,
                            phone: businessPhone,
                        },
                        userId: user_id,
                        service_levels,
                        circles,
                        lookupSkus: callbacks.lookupSkus,
                    })
                        .then((res) => {
                            callbacks.setOrders(res.orders);
                            if (res.skuLookupFailed) {
                                callbacks.setWarningMsg(
                                    "Couldn't load saved SKU details, so items with a SKU have blank dimensions and weight. Fill them in or re-upload the file before submitting."
                                );
                            }
                        })
                        .catch((err) => {
                            console.error(err);
                            callbacks.setErrorMsg(err?.message || `Error processing CSV`);
                            callbacks.setState(SHIPMENT_FORM_STATE.CREATE);
                        })
                        .finally(() => {
                            callbacks.setLoading(false);
                        });
                    break;
                }
                case PROGRESSION_TYPES.PREPARE_JSON: {
                    startJsonUpload({ state, callbacks, userId: user_id, type: getJsonImportType(circles) });
                    break;
                }
                case PROGRESSION_TYPES.PREPARE_MANUAL: {
                    callbacks.setState(SHIPMENT_FORM_STATE.REVIEW);

                    const pendingPrefill = callbacks.pendingForwardStockingRef?.current;
                    if (pendingPrefill) {
                        callbacks.pendingForwardStockingRef.current = null;
                        addOrderWithItems(state.pickupInfo.order_type || 'delivery', pendingPrefill.items);
                    }

                    break;
                }
                case PROGRESSION_TYPES.EDIT_ORDER_PICKUP: {
                    const pickup = state.pickupInfo;
                    callbacks.setOrders((prev) => {
                        return Object.fromEntries(
                            Object.entries(prev).map(([key, order]) => {
                                const swappedAttrs = genAttributes(order, true);
                                const { state: dostate, zip: dozip, city: docity } = genAttributes(order);

                                const pickupInfo = mapPickupInfo(
                                    pickup.is_middle_mile
                                        ? {
                                              pickupcity: order[docity],
                                              pickupstate: order[dostate],
                                              pickupzip: order[dozip],
                                          }
                                        : pickup,
                                    user_id,
                                    swappedAttrs
                                );

                                return [
                                    key,
                                    {
                                        ...order,
                                        ...pickupInfo,
                                    },
                                ];
                            })
                        );
                    });
                }
            }

            callbacks.setProgression({
                current: 0,
                type: null,
            });
        } else {
            callbacks.setProgression((prev) => {
                return { ...prev, current: prev.current + 1 };
            });
        }
    }, [state, callbacks, circles]);

    const revert = useCallback(() => {
        if (state.progression.current === 0 && state.progression.type === PROGRESSION_TYPES.PREPARE_MANUAL) {
            if (callbacks.pendingForwardStockingRef) {
                callbacks.pendingForwardStockingRef.current = null;
            }
        }
        callbacks.setProgression((prev) => {
            if (prev.current > 0) {
                return { ...prev, current: prev.current - 1 };
            }

            return {
                current: 0,
                type: null,
            };
        });
    }, [state]);

    const addOrder = useCallback(
        (type) => {
            // `type` is the order_type resolved by the caller from the pickup dropdown — the single source
            const { hasMissingField, geocodePartialMatch, geocodeFailed, ...created } = createOrder(
                state.pickupInfo,
                state.freightType,
                type,
                user_id,
                circles
            );
            callbacks.setOrders((prev) => ({ ...prev, [created.key]: created }));
            callbacks.setEditing({ order: created });
        },
        [state]
    );

    const addOrderWithItems = useCallback(
        (type, items) => {
            const { hasMissingField, geocodePartialMatch, geocodeFailed, ...created } = createOrder(
                state.pickupInfo,
                state.freightType,
                type,
                user_id,
                circles
            );
            const seeded = {
                ...created,
                forward_stocked_status: 'UNALLOCATED',
                itemsByOrderId: (items || []).map((item) => ({
                    ...DEFAULT_ITEM,
                    item_type: state.freightType,
                    ...item,
                })),
            };
            callbacks.setOrders((prev) => ({ ...prev, [seeded.key]: seeded }));
            callbacks.setEditing({ order: seeded });
        },
        [state]
    );

    const selectFormType = useCallback((form) => {
        let type;
        switch (form) {
            case SHIPMENT_FORM_TYPES.CSV:
                type = PROGRESSION_TYPES.PREPARE_CSV;
                break;
            case SHIPMENT_FORM_TYPES.JSON:
                type = PROGRESSION_TYPES.PREPARE_JSON;
                break;
            case SHIPMENT_FORM_TYPES.MANUAL:
                type = PROGRESSION_TYPES.PREPARE_MANUAL;
                break;
            case SHIPMENT_FORM_TYPES.PDF:
                type = PROGRESSION_TYPES.PREPARE_PDF;
                break;
        }

        callbacks.setFormType(form);
        callbacks.setProgression(() => ({
            type,
            current: 0,
        }));
    }, []);

    const toggleSaveTemplate = useCallback(
        (willSave) =>
            callbacks.setProgression((prev) => {
                return {
                    ...prev,
                    type: willSave ? PROGRESSION_TYPES.PREPARE_CSV_WITH_SAVE : PROGRESSION_TYPES.PREPARE_CSV,
                };
            }),
        []
    );

    // Accept either an object (merged into pickupInfo) or a functional payload
    // (receives prev pickupInfo, returns next). The functional form is needed by
    // callers like PickupInfoModal that want "fill-if-undefined" semantics so they
    // don't clobber user input when re-running their defaults effect.
    const updatePickupInfo = useCallback((payload) => {
        if (typeof payload === 'function') {
            callbacks.setPickupInfo(payload);
        } else {
            callbacks.setPickupInfo((prevState) => ({ ...prevState, ...payload }));
        }
    }, []);

    const editOrder = useCallback(
        (errors, order) => {
            callbacks.setOrders((prev) => ({ ...prev, [order.key]: order }));
        },
        [state, callbacks]
    );

    const deleteOrder = useCallback(
        (order) => {
            callbacks.setOrders((prev) => {
                const { [order.key]: _, ...remaining } = prev;
                return remaining;
            });
        },
        [state, callbacks]
    );

    const openEditModal = useCallback((order, tab) => {
        callbacks.setEditing({ order, tab, dirtyStart: true });
    }, []);

    const openEditPickupModal = useCallback((orders) => {
        callbacks.setEditingPickup({ orders, dirtyStart: true });
    }, []);

    const submit = useCallback(
        //initial creation of order
        ({ oms: requestedOms }) => {
            // Admin carriers always create marketplace (oms:false) orders, regardless of
            // which submit button was used or the form type (CSV or manual).
            const oms = isCarrierAdmin ? false : requestedOms;
            const orders = Object.entries(state.orders).map(([, order]) => ({
                ...order,
                oms,
                planning: false,
            }));

            const missingFieldsByOrder = {};
            let catalogValidationMessage = null;
            let lineIdValidationMessage = null;
            const validated = orders.map((order) => {
                const reachable = reachableImportTabs(state, order.order_type);
                const duplicateLineIds = findDuplicateLineIds(order.itemsByOrderId || []);
                if (duplicateLineIds.length > 0 && !lineIdValidationMessage) {
                    lineIdValidationMessage = `Duplicate Line ID(s) in order ${
                        order.po_number
                    }: ${duplicateLineIds.join(', ')}. Line IDs must be unique within an order.`;
                }
                const serviceCatalog = getServiceCatalogForOrder(order, catalogs, catalogFallback);
                const catalogState = getServiceCatalogCacheStateForOrder(order, catalogs, errors, catalogFallback);
                const missingCatalogFields = getMissingServiceCatalogFields(order, catalogs, errors, catalogFallback);
                const contact = genAttributes(order);
                const pickup = genAttributes(order, true);
                const origin = genAttributes(order, 'ORIGIN');
                const fieldsByTab = genAttributesPerTab({
                    contact,
                    pickup,
                    origin,
                });

                const [validated, hasMissingField] = validate(order, [], {
                    disableGeocoding: true,
                    invalidServiceLines: getInvalidServiceLines(order, serviceCatalog),
                });
                if (missingCatalogFields.length > 0 && !catalogValidationMessage) {
                    catalogValidationMessage = catalogState.error
                        ? 'Unable to load the service catalog. Please refresh and try again.'
                        : 'Service catalog is still loading. Please try again in a moment.';
                }

                // Non-field submit blockers
                const missing = [
                    ...new Set([
                        ...(hasMissingField || []),
                        ...missingCatalogFields,
                        ...geocodeFailedAddressFields(order, contact, pickup, origin),
                        ...missingShipperFields(order, { isCarrierAdmin, userId: user_id }),
                    ]),
                ];
                if (missing.length > 0) {
                    const { reachableMissing, byTab } = missingFieldsByReachableTab(fieldsByTab, missing, reachable);
                    if (reachableMissing.length > 0) {
                        missingFieldsByOrder[validated.key] = byTab;
                    }
                }

                return validated;
            });

            callbacks.setOrders(Object.fromEntries(orders.map((order) => [order.key, order])));

            if (Object.keys(missingFieldsByOrder).length > 0 || lineIdValidationMessage) {
                callbacks.setErrorMsg(
                    lineIdValidationMessage ||
                        catalogValidationMessage ||
                        'Missing fields must be filled in before submitting'
                );
                return;
            }

            callbacks
                .submitOrders({
                    client_id: isCarrierAdmin && state.pickupInfo?.shipper_id ? state.pickupInfo.shipper_id : user_id,
                    orders: finalizeOrders(validated, {
                        oms,
                        formType: state.formType,
                        templateName: state.templateName,
                        userId: user_id,
                    }),
                    filename: state.uploadedFile?.name || undefined,
                    type: jobTypeForForm(state.formType),
                })
                .then(() => {
                    reset();
                });
        },
        [state, callbacks, catalogs, errors, catalogFallback, reset, isCarrierAdmin]
    );

    return {
        advance,
        openEditModal,
        openEditPickupModal,
        reset,
        revert,
        selectFormType,
        toggleSaveTemplate,
        updatePickupInfo,
        addOrder,
        addOrderWithItems,
        editOrder,
        deleteOrder,
        submit,
    };
};
